/**
 * Vorlage „Formen“.
 *
 * Die flache Hand rollt eine Portion Teig oder Hackmasse in kleinen Kreisen
 * zu einer glatten Kugel. Bei Frikadellen drückt sie die Kugel danach flach.
 * Links liegen die schon geformten Stücke.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { doughBall } from '../parts/food.ts';
import { hand } from '../parts/hand.ts';
import { arcArrow, arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { place } from '../parts/utensils.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Brett und Stelle, an der geformt wird. */
const BOARD = { x: 14, width: 212, height: 7 } as const;
const BOARD_TOP = GROUND_Y - BOARD.height;
const WORK_X = 150;
/** Fertige Stücke links. */
const DONE_X = [40, 84] as const;

/** Was geformt wird. */
const FORMS = { balls: 'Kugeln', patties: 'Frikadellen' } as const;
type Form = keyof typeof FORMS;

/** Maße: Kugel und flach gedrückt. */
const BALL = { width: 34, height: 30 } as const;
const PATTY = { width: 50, height: 13 } as const;

/** Zeitplan für Frikadellen. */
const TIME = { roll: [0, 0.4], press: [0.44, 0.6], lift: [0.62, 0.72], reset: [0.86, 1] } as const;

/** Teigstück; tone hell für Teig, dunkler für Hackmasse. */
function piece(cx: number, width: number, height: number, form: Form, turn: number): SceneNode[] {
  const outline = doughBall(cx, BOARD_TOP, width, height, 3);
  const tone = form === 'patties' ? 'shade' : 'tint';
  // Eine Naht auf der Kugel dreht sich beim Rollen mit.
  const seamX = cx + Math.sin(turn) * width * 0.3;
  return [
    shape(outline, styles.solid(tone)),
    group(
      [
        shape(
          path()
            .move(seamX - 5, BOARD_TOP - height * 0.75)
            .quad(seamX, BOARD_TOP - height * 0.62, seamX + 5, BOARD_TOP - height * 0.75)
            .toString(),
          { stroke: form === 'patties' ? 'deep' : 'shade', strokeWidth: STROKE.detail },
        ),
      ],
      { clip: outline },
    ),
  ];
}

/** Vorlage „Formen“. */
export const prepShapeTemplate = defineTemplate({
  id: 'prep.shape',
  group: 'prep',
  title: 'Formen',
  description:
    'Mit leicht angefeuchteten Händen wird die Masse portioniert und in kreisenden Bewegungen zu Kugeln gerollt. Frikadellen werden danach flach gedrückt.',
  durationSeconds: 5,
  keywords: [
    'formen',
    'zu kugeln',
    'kugeln formen',
    'kugeln rollen',
    'bällchen',
    'frikadellen',
    'buletten',
    'fleischpflanzerl',
    'klöße',
    'knödel',
    'flach drücken',
    'brötchen formen',
  ],
  stillTime: 0.2,
  parameters: [choice('form', 'Form', FORMS, 'balls')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/frikadell|bulette|burger|pflanzerl|flach drücken|patties/.test(lower)) return { form: 'patties' };
    if (/kugel|bällchen|klöße|knödel|brötchen|rollen/.test(lower)) return { form: 'balls' };
    return {};
  },
  scene(values, t) {
    const form = textValue(values, 'form', 'balls') as Form;
    let width: number = BALL.width;
    let height: number = BALL.height;
    let handX: number;
    let handY: number;
    let turn: number;

    if (form === 'balls') {
      // Die Hand kreist ständig; drei Umläufe je Schleife.
      const angle = t * 3 * Math.PI * 2;
      handX = WORK_X - 20 + Math.cos(angle) * 8;
      handY = BOARD_TOP - height - 1 + Math.sin(angle) * 2;
      turn = angle;
    } else {
      const roll = segment(t, ...TIME.roll);
      const angle = roll * 2 * Math.PI * 2;
      const press = ease.inOutCubic(segment(t, ...TIME.press));
      const reset = ease.inOutSine(segment(t, ...TIME.reset));
      const flat = press * (1 - reset);
      width = lerp(BALL.width, PATTY.width, flat);
      height = lerp(BALL.height, PATTY.height, flat);
      const lift = ease.inOutCubic(segment(t, ...TIME.lift)) * (1 - reset) * 26;
      handX = WORK_X - 20 + Math.cos(angle) * 8 * (1 - flat);
      handY = BOARD_TOP - height - 1 - lift + Math.sin(angle) * 2 * (1 - flat);
      turn = angle;
    }

    const done = form === 'patties' ? PATTY : BALL;
    return compact([
      ...counter(),
      shadow(BOARD.x + BOARD.width / 2, BOARD.width * 0.5),
      rect(BOARD.x, BOARD_TOP, BOARD.width, BOARD.height, 3, styles.solid('paper')),
      ...DONE_X.flatMap((x) => piece(x, done.width, done.height, form, 0)),
      ...piece(WORK_X, width, height, form, turn),
      faded(1, [place(handX, handY, 0, hand())]),
    ]);
  },
  hints(values) {
    if (textValue(values, 'form', 'balls') === 'patties') {
      return [
        arrow([
          { x: WORK_X + 40, y: BOARD_TOP - 60 },
          { x: WORK_X + 40, y: BOARD_TOP - 30 },
        ]),
      ];
    }
    return [arcArrow(WORK_X, BOARD_TOP - BALL.height - 16, 26, 8, 200, 340)];
  },
});
